import { useMemo, useState } from "react"
import { Box, HStack, Text } from "@chakra-ui/react"
import { FaCircleInfo } from "react-icons/fa6"
import { NativeSelectField, NativeSelectRoot } from "@hydrologiq/shared/chakra/NativeSelect"
import { ActionButton } from "@hydrologiq/shared/content/components/Button"
import { Tooltip } from "@hydrologiq/shared/chakra/Tooltip"
import { Link as SharedLink } from "@hydrologiq/shared/components/Link"
import { SystemAsset } from "../../providers/MapAssetDataProvider"
import { useMapAssetDataStatus } from "../../providers/MapAssetDataProvider"
import { DeviceSource } from "../../hooks/useDataConnectionMapping"
import {
  filterAssetsBySourceClass,
  filterSourcesByAssetClass,
  sourceFitsAsset,
} from "@hydrologiq/shared/data-connections/sourceFilterUtils"
import { sourceDisplayLabel } from "@hydrologiq/shared/data-connections/sourceLabels"
import { formatAssetType } from "./mappingUtils"
import { ADD_ROW_BG, ADD_ROW_BORDER, getConnectorConfig } from "@hydrologiq/shared/data-connections/constants"

export interface AddMappingRowProps {
  availableAssets: SystemAsset[]
  availableSources: DeviceSource[]
  onAdd: (assetId: string, sourceRef: string) => void
}

export function AddMappingRow({
  availableAssets,
  availableSources,
  onAdd,
}: AddMappingRowProps) {
  const [draftAsset, setDraftAsset] = useState("")
  const [draftSource, setDraftSource] = useState("")

  const { connectorType } = useMapAssetDataStatus()
  const connectorConfig = getConnectorConfig(connectorType)

  const hasAssets = availableAssets.length > 0
  const hasSources = availableSources.length > 0

  const selectedAsset = availableAssets.find((a) => a.id === draftAsset)
  const selectedAssetClass = selectedAsset?.assetClass ?? null

  const selectedSource = availableSources.find((s) => s.ref === draftSource)

  // Filter sources to those that fit the selected asset (when an asset is selected)
  const filteredSources = useMemo(() => {
    if (!draftAsset) return availableSources
    const filtered = filterSourcesByAssetClass(availableSources, selectedAssetClass, [], draftAsset)
    return filtered.filter((s) => !s.disabled)
  }, [availableSources, draftAsset, selectedAssetClass])

  // Filter assets to those that fit the selected source (when a source is selected)
  const filteredAssets = useMemo(() => {
    if (!draftSource || !selectedSource) return availableAssets
    return filterAssetsBySourceClass(availableAssets, selectedSource.compatibleClasses, selectedSource.fittingAssetIds)
  }, [availableAssets, draftSource, selectedSource])

  // If the current draft source is no longer valid after class filtering, treat it as unselected
  const validDraftSource = filteredSources.some((s) => s.ref === draftSource) ? draftSource : ""

  // If the current draft asset is no longer valid after reverse class filtering, treat it as unselected
  const validDraftAsset = filteredAssets.some((a) => a.id === draftAsset) ? draftAsset : ""

  const assetItems = [
    { value: "", label: hasAssets ? "Select an asset..." : "No assets available" },
    ...filteredAssets.map((asset) => ({ value: asset.id, label: asset.name })),
  ]

  const sourceItems = [
    { value: "", label: hasSources ? "Select a source..." : "No sources available" },
    ...filteredSources.map((source) => ({ value: source.ref, label: sourceDisplayLabel(source, availableSources) })),
  ]

  const canAdd = validDraftAsset !== "" && validDraftSource !== ""

  const handleAdd = () => {
    if (!canAdd) return
    onAdd(validDraftAsset, validDraftSource)
    setDraftAsset("")
    setDraftSource("")
  }

  const handleAssetChange = (value: string) => {
    setDraftAsset(value)
    // Only reset source if it's no longer compatible with the new asset
    if (value && draftSource) {
      const newAsset = availableAssets.find((a) => a.id === value)
      const selectedSrc = availableSources.find((s) => s.ref === draftSource)
      if (newAsset && selectedSrc && !sourceFitsAsset(selectedSrc, newAsset)) setDraftSource("")
    }
  }

  const handleSourceChange = (value: string) => {
    setDraftSource(value)
    // Only reset asset if it's no longer compatible with the new source
    if (value && draftAsset) {
      const newSource = availableSources.find((s) => s.ref === value)
      const currentAsset = availableAssets.find((a) => a.id === draftAsset)
      if (newSource && currentAsset) {
        const fits = filterAssetsBySourceClass([currentAsset], newSource.compatibleClasses, newSource.fittingAssetIds)
        if (fits.length === 0) setDraftAsset("")
      }
    }
  }

  // Helper text for filter context — only show when one field is selected but not both
  const filterText = useMemo(() => {
    // Don't show filter text when both are selected — the user is ready to submit
    if (validDraftAsset && validDraftSource) return null

    if (draftSource && selectedSource && selectedSource.compatibleClasses.length > 0 && !validDraftAsset) {
      const formatted = selectedSource.compatibleClasses.map(c => formatAssetType(c)).join(", ")
      const noMatches = filteredAssets.length === 0
      return {
        message: <>Only showing <strong>{formatted}</strong> assets compatible with <strong>{selectedSource.label}</strong>.</>,
        noAssets: noMatches,
        noSources: false,
      }
    }
    if (draftAsset && selectedAsset && selectedAssetClass && !validDraftSource) {
      const formatted = formatAssetType(selectedAssetClass)
      const noMatches = filteredSources.length === 0
      return {
        message: <>Only showing sources compatible with <strong>{formatted}</strong>.</>,
        noAssets: false,
        noSources: noMatches,
      }
    }
    return null
  }, [draftAsset, draftSource, selectedAsset, selectedAssetClass, selectedSource, filteredAssets.length, filteredSources.length, validDraftAsset, validDraftSource])

  return (
    <Box
      bg={ADD_ROW_BG}
      borderRadius="md"
      border="1px solid"
      borderColor={ADD_ROW_BORDER}
      p={4}
    >
      <HStack gap={4} align="end">
        <Box flex={1}>
          <Text fontSize="sm" fontWeight="medium" mb={1}>Asset</Text>
          <NativeSelectRoot disabled={!hasAssets}>
            <NativeSelectField
              value={validDraftAsset}
              onChange={(e) => handleAssetChange(e.target.value)}
              items={assetItems}
            />
          </NativeSelectRoot>
        </Box>
        <Box flex={1}>
          <HStack gap={1} mb={1} align="center">
            <Text fontSize="sm" fontWeight="medium">Source</Text>
            <Tooltip
              content={connectorConfig.sourceTooltip}
              contentProps={{ css: { "--tooltip-bg": "white", color: "primary", textTransform: "none" } }}
            >
              <Box color="fg.muted" cursor="help" display="inline-flex">
                <FaCircleInfo style={{ fontSize: "11px" }} />
              </Box>
            </Tooltip>
          </HStack>
          <NativeSelectRoot disabled={!hasSources}>
            <NativeSelectField
              value={validDraftSource}
              onChange={(e) => handleSourceChange(e.target.value)}
              items={sourceItems}
            />
          </NativeSelectRoot>
        </Box>
        <ActionButton
          onClick={handleAdd}
          disabled={!canAdd}
          context={{
            label: "+ Add mapping",
            description: "Map the selected asset to the selected source",
            variant: "primary",
          }}
        />
      </HStack>
      {filterText && (
        <Text fontSize="xs" color="fg.muted" mt={2}>
          {filterText.message}
          {filterText.noAssets && (
            <> No compatible assets found in this system. <SharedLink href="/assets" fontSize="xs">Create or assign an asset</SharedLink> to this system to continue.</>
          )}
          {filterText.noSources && (
            <> No compatible sources are available for this asset type.</>
          )}
        </Text>
      )}
    </Box>
  )
}

export default AddMappingRow
